import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, Trash2, Settings, ExternalLink, AlertCircle } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DisconnectAccountDialog } from './DisconnectAccountDialog';
import { PartnerAccountSettingsDialog } from './PartnerAccountSettingsDialog';
import { ReconnectAccountDialog } from './ReconnectAccountDialog';
import { SyncStatusBadge } from './SyncStatusBadge';
import { SyncControlButtons } from './SyncControlButtons';
import { EnableDisableToggle } from './EnableDisableToggle';
import { DisabledAccountBadge } from './DisabledAccountBadge';
import type { PartnerAccount, ConnectionStatus } from '@/types/api';

interface PartnerAccountCardProps {
  account: PartnerAccount;
}

function getConnectionStatusBadge(status: ConnectionStatus) {
  switch (status) {
    case 'connected':
      return <Badge variant="success">Connected</Badge>;
    case 'disconnected':
      return <Badge variant="secondary">Disconnected</Badge>;
    case 'error':
      return <Badge variant="destructive">Error</Badge>;
  }
}

export function PartnerAccountCard({ account }: PartnerAccountCardProps): JSX.Element {
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isReconnectDialogOpen, setIsReconnectDialogOpen] = useState(false);

  return (
    <>
      <Card data-testid={`partner-account-card-${account.id}`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Link to={`/partner-accounts/${account.id}`} className="hover:underline">
              <CardTitle className="text-lg font-medium">{account.name}</CardTitle>
            </Link>
            {!account.enabled && <DisabledAccountBadge />}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`partner-account-menu-${account.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild data-testid={`partner-account-details-${account.id}`}>
                <Link to={`/partner-accounts/${account.id}`}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Details
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setIsSettingsDialogOpen(true); }}
                data-testid={`partner-account-settings-${account.id}`}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => { setIsDisconnectDialogOpen(true); }}
                data-testid={`partner-account-disconnect-${account.id}`}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Platform:</span>
              <span className="text-sm font-medium">{account.partnerType?.name ?? 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Connection:</span>
              {getConnectionStatusBadge(account.connectionStatus)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Sync:</span>
              <SyncStatusBadge status={account.syncStatus} />
            </div>
            {account.locationCounts && (
              <div className="grid grid-cols-3 gap-2 rounded-lg border bg-muted/30 p-2">
                <div className="text-center">
                  <div className="text-lg font-semibold">{account.locationCounts.total}</div>
                  <div className="text-xs text-muted-foreground">Locations</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{account.locationCounts.enriched}</div>
                  <div className="text-xs text-muted-foreground">Enriched</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{account.locationCounts.published}</div>
                  <div className="text-xs text-muted-foreground">Published</div>
                </div>
              </div>
            )}
            {account.syncStats?.recentErrorCount && account.syncStats.recentErrorCount > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-2">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-destructive">
                    {account.syncStats.recentErrorCount} error{account.syncStats.recentErrorCount === 1 ? '' : 's'} in last 7 days
                  </span>
                  {account.syncStats.recentErrors[0] && (
                    <p className="text-xs text-destructive/80 truncate mt-0.5">
                      {account.syncStats.recentErrors[0]}
                    </p>
                  )}
                </div>
                <Link
                  to={`/partner-accounts/${account.id}`}
                  className="text-xs text-destructive underline flex-shrink-0"
                >
                  View details
                </Link>
              </div>
            )}
            {account.lastSyncAt && (
              <div className="text-xs text-muted-foreground">
                Last synced: {new Date(account.lastSyncAt).toLocaleString()}
              </div>
            )}
            {account.syncStatus === 'paused' && account.nextSyncAt && (
              <div className="text-xs text-amber-600 font-medium">
                Resumes in ~{Math.max(1, Math.ceil((new Date(account.nextSyncAt).getTime() - Date.now()) / 3600000))} hour{Math.ceil((new Date(account.nextSyncAt).getTime() - Date.now()) / 3600000) === 1 ? '' : 's'}
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <SyncControlButtons
                accountId={account.id}
                syncStatus={account.syncStatus}
                connectionStatus={account.connectionStatus}
                disabled={!account.enabled}
              />
              <EnableDisableToggle
                accountId={account.id}
                accountName={account.name}
                enabled={account.enabled}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <DisconnectAccountDialog
        account={account}
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      />

      <PartnerAccountSettingsDialog
        account={account}
        open={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
        onReconnectClick={() => { setIsReconnectDialogOpen(true); }}
      />

      <ReconnectAccountDialog
        account={account}
        open={isReconnectDialogOpen}
        onOpenChange={setIsReconnectDialogOpen}
      />
    </>
  );
}
